import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { clientSchema, type ClientFormData, IMPLEMENTATION_TIERS } from '../schemas/clientSchema'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'

interface ClientFormProps {
  initialData?: any
  onSubmit: (data: ClientFormData) => Promise<void>
  onCancel: () => void
  loading?: boolean
}

export function ClientForm({ initialData, onSubmit, onCancel, loading }: ClientFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      status: 'active',
      implementation_tier: 'tier1',
      implementation_fee: 1500,
      baseline_ar_total: 0,
      ...initialData,
    },
  })

  const handleTierChange = (tier: string) => {
    const tierData = IMPLEMENTATION_TIERS.find(t => t.value === tier)
    if (tierData) {
      setValue('implementation_fee', tierData.fee)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input label="Client Name" {...register('name')} error={errors.name?.message} required />
      <div className="grid grid-cols-2 gap-4">
        <Input label="Contact Person" {...register('contact_person')} error={errors.contact_person?.message} />
        <Input label="Phone" type="tel" {...register('phone')} error={errors.phone?.message} />
      </div>
      <Input label="Email" type="email" {...register('email')} error={errors.email?.message} />
      <Input
        label="Contract Start Date"
        type="date"
        {...register('contract_start_date')}
        error={errors.contract_start_date?.message}
        helperText="Fees apply only to payments received on or after this date"
        required
      />
      <Input label="Baseline AR Total" type="number" step="0.01" {...register('baseline_ar_total')} error={errors.baseline_ar_total?.message} required />
      <Select
        label="Implementation Tier"
        {...register('implementation_tier')}
        options={IMPLEMENTATION_TIERS.map(t => ({ value: t.value, label: t.label }))}
        onChange={(e) => {
          register('implementation_tier').onChange(e)
          handleTierChange(e.target.value)
        }}
        error={errors.implementation_tier?.message}
        required
      />
      <Input label="Implementation Fee" type="number" step="0.01" {...register('implementation_fee')} error={errors.implementation_fee?.message} required />
      <Select
        label="Status"
        {...register('status')}
        options={[
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
          { value: 'pending', label: 'Pending' },
        ]}
        error={errors.status?.message}
      />
      <div className="flex justify-end space-x-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={loading}>{initialData ? 'Update Client' : 'Create Client'}</Button>
      </div>
    </form>
  )
}
